import React, { useState } from 'react';
import styled from 'styled-components';

import { Blurb } from 'components/Containers';
import ProjectedDischargeModal from 'components/modals/ProjectedDischargeModal';
import AccessGuard from 'components/shared/AccessGuard';
import Tooltip from 'components/shared/Tooltip';
import { useTranslation } from 'context/translations';
import { simpleDate } from 'lib/date';
import Episode from 'models/Episode';
import LocationEpisode from 'models/LocationEpisode';
import Profile, { PERMISSIONS } from 'models/Profile';
import { colors } from 'styles/theme/colors';
import { BodySmallBold, H3, Label, LabelBold } from 'styles/typography';
import InfoIconOutline from 'svg/InfoIconOutline';
import PencilIcon from 'svg/PencilIcon';

type ProjectedDischargeType = {
  locationEpisode: LocationEpisode;
  episode: Episode;
  patientName: string;
  profile: Profile;
};

export function ProjectedDischarge(props: ProjectedDischargeType) {
  const [showModal, setShowModal] = useState(false);
  const { translate } = useTranslation();

  const { locationEpisode, episode, patientName, profile } = props;
  const projectedDischargeReview = locationEpisode.projectedDischargeReview;

  const currentGroupTypeClientConfig = locationEpisode?.owner?.client?.configForManagedGroupType(
    locationEpisode?.group?.groupTypeId
  );

  const showProjectedDischarge =
    (profile.has(PERMISSIONS.reviewProjectedDischargeCreate, { episode, locationEpisode }) &&
      currentGroupTypeClientConfig?.projectedDischarge.enabled &&
      !locationEpisode?.discharged &&
      !episode?.archived &&
      !locationEpisode?.archived) ||
    (projectedDischargeReview && !projectedDischargeReview?.pending);

  const showUpdate =
    (profile.isManagerFor(episode) || profile.belongsToOwningClientFor(episode)) &&
    !locationEpisode.archived &&
    locationEpisode.latest &&
    !episode.archived;

  const date = projectedDischargeReview?.date;
  const planningDischargeTo = projectedDischargeReview?.planningDischargeTo;

  return !showProjectedDischarge ? (
    <></>
  ) : (
    <Blurb data-cy='projectedDischargeDate'>
      <DischargeHeader>
        <H3>{translate('Discharge')} Planning</H3>
        <Spacer />
        {showUpdate && (
          <AccessGuard
            accessor={
              projectedDischargeReview
                ? PERMISSIONS.reviewProjectedDischargeEdit
                : PERMISSIONS.reviewProjectedDischargeCreate
            }
            locationEpisode={locationEpisode}
            episode={episode}>
            <ActionContainer data-cy='clickable' onClick={() => setShowModal(true)}>
              <PencilIcon width={12} height={12} color={colors.primaryBlue} />
              <StyledLabel>Update</StyledLabel>
            </ActionContainer>
          </AccessGuard>
        )}
      </DischargeHeader>
      <FieldsContainer>
        <FieldRow>
          <FieldLabelWithTooltip>
            <FieldLabel>Projected {translate('Discharge')}</FieldLabel>
            <Tooltip text='This date is provided by the risk-bearing entity' position='right'>
              <StyledInfoIcon />
            </Tooltip>
          </FieldLabelWithTooltip>
          <FieldValue $isOverdue={locationEpisode.dischargeOverdue}>{date ? simpleDate(date) : '—'}</FieldValue>
        </FieldRow>
        <FieldRow>
          <FieldLabel>Planning {translate('Discharge')} To</FieldLabel>
          <FieldValue $isOverdue={false}>{planningDischargeTo || '—'}</FieldValue>
        </FieldRow>
      </FieldsContainer>
      {showModal && (
        <ProjectedDischargeModal
          review={projectedDischargeReview}
          setShow={setShowModal}
          patientName={patientName}
          locationEpisode={locationEpisode}
        />
      )}
    </Blurb>
  );
}

const DischargeHeader = styled.div`
  display: flex;
  padding: 24px;
  align-items: center;

  h3 {
    font-size: 14px;
  }
`;

const StyledInfoIcon = styled(InfoIconOutline)`
  width: 14px;
  height: 14px;
  font-size: 14px;
  margin-left: 4px;
  color: ${colors.black75};
`;

const Spacer = styled.div`
  flex: 1;
`;

const StyledLabel = styled(LabelBold)`
  margin-left: 8px;
  cursor: pointer;
  color: var(--primary-blue);
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const FieldsContainer = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-top: 1px solid var(--black-10);
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabelWithTooltip = styled.div`
  display: flex;
  align-items: center;
`;

const FieldLabel = styled(Label)`
  color: ${colors.black};
`;

const FieldValue = styled(BodySmallBold)<{ $isOverdue: boolean }>`
  color: ${(props) => (props.$isOverdue ? 'var(--accent-red)' : 'inherit')};
`;
